import { documentBodyContent, PDF_PAGE_SETUP, loadPdfMake } from './documentPdf';
import { resolveExportBodyHtml } from '../utils/resolveDocMediaForExport';

/**
 * A whole folder → ONE PDF.
 *
 * WHY THIS IS A SMALL FEATURE AND NOT A BIG ONE. `buildPdfDocDefinition`
 * already returns a pdfmake docDefinition whose `content` is a flat ARRAY of
 * nodes, and pdfmake honours `pageBreak: 'before'` on any node. So combining N
 * documents is concatenating N content arrays with a break between them —
 * there is no second layout engine, no merging of finished PDFs (which would
 * need pdf-lib and would lose the outline), and no second HTML mapper. The
 * per-document walk is imported from documentPdf.js precisely so the two
 * exports cannot drift.
 *
 * THE FOUR DECISIONS worth stating, because each could reasonably have gone the
 * other way:
 *
 * 1. NESTED FOLDERS ARE INCLUDED, depth-first, in the order the tree displays.
 *    "All documents from that folder" is a promise a reader will check, and
 *    quietly skipping a subfolder breaks it in the way that is hardest to
 *    notice — the PDF looks complete.
 * 2. SUBFOLDER NAMES BECOME HEADINGS. Flattening loses the only structure the
 *    user built. A heading costs one line and keeps it.
 * 3. EACH DOCUMENT STARTS ON A NEW PAGE. That is what "combined" means to
 *    someone printing it; running them together makes the boundaries invisible.
 * 4. A TABLE OF CONTENTS when there is more than one document, using pdfmake's
 *    native `toc`/`tocItem` so page numbers are real. A 40-document folder is
 *    unusable without it.
 *
 * WHAT IS CAPPED, AND LOUDLY. pdfmake lays out on the MAIN THREAD, and a
 * document may embed base64 images up to 4 MB each. A folder of hundreds would
 * freeze the window for a long time with no way to cancel. So the export stops
 * at `MAX_COMBINED_DOCUMENTS` and RETURNS the number it skipped — the caller
 * must tell the user. A silent truncation would produce a PDF that looks whole
 * and is not, which is the same failure as (1).
 */

export const MAX_COMBINED_DOCUMENTS = 150;

/** How many `readDocument` calls are in flight at once. */
const READ_CONCURRENCY = 4;

/**
 * Same ordering the sidebar renders: sortKey first, then name, then id.
 * Mirrors `cmpNodes` in DocumentsSidebar.js — a PDF whose order disagrees with
 * the tree reads as scrambled even though every document is present.
 */
function cmpNodes(a, b) {
  if (a.sortKey !== b.sortKey) return a.sortKey < b.sortKey ? -1 : 1;
  const an = (a.name || '').toLowerCase();
  const bn = (b.name || '').toLowerCase();
  if (an !== bn) return an < bn ? -1 : 1;
  return String(a.id) < String(b.id) ? -1 : 1;
}

/**
 * The folder's subtree, flattened into export order.
 *
 * Returns `[{ id, name, type, depth }]` — documents to read, folders as section
 * headings. Pure: it takes the flat node list `listTree` already returns, so it
 * is testable without touching Tauri.
 *
 * Cycle-safe. A parent chain can only be corrupted by a bad write or a
 * hand-edited store, but a cycle here is an infinite loop that hangs the app
 * rather than an error anyone can read, so `seen` is cheap insurance.
 */
export function collectFolderSubtree(nodes, folderId) {
  const byParent = new Map();
  for (const n of Array.isArray(nodes) ? nodes : []) {
    if (!n || n.id == null) continue;
    const key = n.parentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(n);
  }
  for (const arr of byParent.values()) arr.sort(cmpNodes);

  const out = [];
  const seen = new Set();
  const walk = (parentId, depth) => {
    if (seen.has(parentId)) return;
    seen.add(parentId);
    for (const node of byParent.get(parentId) || []) {
      if (node.type === 'folder') {
        out.push({ id: node.id, name: node.name || 'Untitled folder', type: 'folder', depth });
        walk(node.id, depth + 1);
      } else {
        out.push({ id: node.id, name: node.name || 'Untitled', type: 'document', depth });
        // Documents can nest documents (subpages). Same promise as (1) in the
        // header: "all documents from that folder" includes them, depth-first,
        // one level deeper — silently omitting a subpage is the failure that
        // looks complete.
        walk(node.id, depth + 1);
      }
    }
  };
  walk(folderId, 0);
  return out;
}

/** Run `fn` over `items` with at most `limit` in flight, preserving order. */
async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i], i);
    }
  });
  await Promise.all(workers);
  return out;
}

/**
 * Entries → one docDefinition.
 *
 * `entries` is the subtree with document bodies filled in:
 *   [{ type: 'folder'|'document', name, depth, bodyHtml? }]
 *
 * Exported for testing — asserting the shape of this object is how the page
 * breaks and the TOC get verified without rendering a real PDF.
 */
export function buildFolderPdfDocDefinition({
  folderName = 'Documents',
  entries = [],
  includeToc = true,
  includePageNumbers = true,
} = {}) {
  const docs = entries.filter((e) => e.type === 'document');
  const content = [{ text: folderName || 'Documents', style: 'title' }];

  // The TOC earns its page only when there is something to navigate — asking
  // for one over a single document produces a contents page with one line on
  // it. So the option is a ceiling, not an override.
  const withToc = includeToc && docs.length > 1;
  if (withToc) {
    content.push({ toc: { title: { text: 'Contents', style: 'h2' } } });
  }

  let firstDocSeen = false;
  for (const entry of entries) {
    if (entry.type === 'folder') {
      // Depth-scaled so nesting survives: a sub-sub-folder should not look like
      // a top-level section. Clamped at h4 — pdfmake has no h5 style here and
      // an unknown style name silently renders unstyled.
      const style = ['h1', 'h2', 'h3', 'h4'][Math.min(entry.depth, 3)];
      content.push({ text: entry.name, style, margin: [0, 14, 0, 4] });
      continue;
    }

    const heading = {
      text: entry.name || 'Untitled',
      style: ['h1', 'h2', 'h3', 'h4'][Math.min(entry.depth, 3)],
    };
    if (withToc) heading.tocItem = true;
    // Every document after the first starts a page. The FIRST one breaks only
    // when a TOC precedes it — otherwise the PDF opens on a blank page, which
    // is the classic off-by-one in this pattern.
    if (firstDocSeen || withToc) heading.pageBreak = 'before';
    firstDocSeen = true;

    content.push(heading);
    content.push(...documentBodyContent(entry.bodyHtml || ''));
  }

  // `delete`, not `footer: undefined`: pdfmake reads the KEY, and a present-
  // but-undefined footer is not the same as no footer to a library that checks
  // `'footer' in docDefinition`. Copying first leaves the shared setup intact
  // for the single-document export, which still always numbers its pages.
  const setup = { ...PDF_PAGE_SETUP };
  if (!includePageNumbers) delete setup.footer;

  return {
    info: { title: folderName || 'Documents' },
    content,
    ...setup,
  };
}

/**
 * Read every document in the folder and render the combined PDF.
 *
 * @param {object} args
 * @param {Array} args.nodes      the flat tree from `listTree`
 * @param {string} args.folderId  the folder to export
 * @param {string} args.folderName
 * @param {Function} args.readDocument  `(id) => Promise<{ title, bodyHtml }>`
 * @param {Function} [args.onProgress]  `(done, total) => void`
 * @returns {Promise<{ bytes: Uint8Array, documentCount: number, skipped: number }>}
 *          `skipped` > 0 means the cap was hit and the CALLER MUST SAY SO.
 */
export async function renderFolderPdf({
  nodes,
  folderId,
  folderName,
  readDocument,
  onProgress,
  includeToc = true,
  includePageNumbers = true,
}) {
  const subtree = collectFolderSubtree(nodes, folderId);
  const docs = subtree.filter((e) => e.type === 'document');
  if (!docs.length) {
    // Refused rather than emitting a title page with nothing behind it — a
    // one-page PDF containing only a folder name reads as a broken export.
    throw new Error('This folder has no documents to export.');
  }

  const included = new Set(docs.slice(0, MAX_COMBINED_DOCUMENTS).map((d) => d.id));
  const skipped = docs.length - included.size;

  let done = 0;
  const bodies = await mapLimit([...included], READ_CONCURRENCY, async (id) => {
    let doc = null;
    try {
      doc = await readDocument(id);
      // Encrypted images -> data: URIs per document, inside the same
      // concurrency cap as the reads (the fetch+decrypt is the other slow,
      // per-document step). The resolver removes what it cannot resolve
      // rather than throwing, so this cannot cost the document its text.
      if (doc) doc = { ...doc, bodyHtml: await resolveExportBodyHtml(doc.bodyHtml) };
    } catch {
      // One unreadable document must not lose the other 149. It is dropped
      // from the PDF rather than silently rendered blank — an empty section
      // under a real heading looks like the document itself was empty.
      doc = null;
    }
    onProgress?.(++done, included.size);
    return [id, doc];
  });
  const bodyById = new Map(bodies);

  const entries = subtree
    .filter((e) => e.type === 'folder' || (included.has(e.id) && bodyById.get(e.id)))
    .map((e) =>
      e.type === 'folder'
        ? e
        : { ...e, name: bodyById.get(e.id)?.title || e.name, bodyHtml: bodyById.get(e.id)?.bodyHtml || '' }
    );

  // A folder heading with nothing under it after the drops above is noise.
  const pruned = entries.filter((e, i) => {
    if (e.type !== 'folder') return true;
    return entries.slice(i + 1).some((n) => n.depth > e.depth && n.type === 'document');
  });

  const pdfMake = await loadPdfMake();
  const generator = pdfMake.createPdf(
    buildFolderPdfDocDefinition({ folderName, entries: pruned, includeToc, includePageNumbers })
  );
  const buffer = await Promise.resolve(generator.getBuffer());
  return {
    bytes: new Uint8Array(buffer),
    documentCount: pruned.filter((e) => e.type === 'document').length,
    skipped,
  };
}
